// @ts-nocheck
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XGButton, XGCheckBox, XGDatePicker, XGInput } from 'xg-cl';
import {
  CaretDown,
  CaretUp,
  CaretUpDown,
  Trash,
  WarningCircle,
  PlusCircle
} from '@phosphor-icons/react';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';
import DeleteModal from '../../Trip/DeleteModal/DeleteModal';
import SegmentedToggle from '../../Common/SegmentedToggle/SegmentedToggle';
import Toggle from '../../Common/Toggle/Toggle';
import { ErrorToast, SuccessToast } from '../../../utils/alerts';
import {
  createDiscountCode,
  deleteDiscountCode,
  getDiscountCodes,
  updateDiscountCode
} from '../../../services/discountcode/discountcode';
import {
  DISCOUNT_SCOPE_BOOKING,
  DISCOUNT_SCOPE_PERSON,
  DISCOUNT_TYPE_FIXED,
  DISCOUNT_TYPE_PERCENTAGE,
  EXPIRATION_MODE_DATE,
  EXPIRATION_MODE_DAYS,
  buildEditableDiscountCodePayload,
  createDiscountCodeFormFromItem,
  createEmptyDiscountCodeForm,
  extractDiscountCodeItems,
  formatCompactNumber,
  formatDiscountValue,
  getDiscountCodeId,
  normalizeDiscountCode
} from './discountCodeUtils';
import './DiscountCodesPanel.css';

const SORTABLE_COLUMNS = {
  title: 'title',
  code: 'code'
};

const normalizeCodeValue = (code) =>
  String(code || '')
    .trim()
    .toUpperCase();

const sanitizeDecimalValue = (value) => {
  const sanitized = String(value || '')
    .replace(/[^\d.]/g, '')
    .replace(/(\..*)\./g, '$1');

  if (sanitized.startsWith('.')) return `0${sanitized}`;

  return sanitized;
};

const dateStringToDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day, 12, 0, 0);
};

const dateToDateString = (value) => {
  if (!value || !(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (value) => {
  if (!value) return '';
  const dateSegment = String(value).split('T')[0];
  const [year, month, day] = dateSegment.split('-').map(Number);

  if (!year || !month || !day) return '';

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(new Date(Date.UTC(year, month - 1, day)));
};

const isFormValid = (formValues) => {
  const discountValue = Number(formValues.discountValue);
  const expDays = Number(formValues.expDays);

  if (!formValues.title.trim() || !formValues.code.trim()) return false;
  if (!Number.isFinite(discountValue) || discountValue <= 0) return false;
  if (
    formValues.discountType === DISCOUNT_TYPE_PERCENTAGE &&
    discountValue > 100
  ) {
    return false;
  }
  if (formValues.expMode === EXPIRATION_MODE_DATE) {
    return Boolean(formValues.expirationDate);
  }
  if (formValues.expMode === EXPIRATION_MODE_DAYS) {
    return Number.isFinite(expDays) && expDays > 0;
  }

  return true;
};

const getDeadlineLabel = (discountCode, t) => {
  const normalized = normalizeDiscountCode(discountCode);

  if (normalized.expType === EXPIRATION_MODE_DATE) {
    return formatDateForDisplay(normalized.expirationDateTime);
  }

  if (normalized.expType === EXPIRATION_MODE_DAYS) {
    return t('settings:discountCodes.daysBeforeTripStart', {
      count: normalized.expDays,
      defaultValue: '{{count}} days before trip start date'
    });
  }

  return t('settings:discountCodes.noDeadline', 'No deadline');
};

const DiscountCodeForm = ({
  formValues,
  formMode,
  onCancel,
  onChange,
  onSubmit,
  isSubmitting,
  codeErrorMessage
}) => {
  const { t } = useTranslation(['settings', 'common']);
  const isPercentage = formValues.discountType === DISCOUNT_TYPE_PERCENTAGE;
  const discountValue = Number(formValues.discountValue || 0);
  const sampleAmount = Math.max(discountValue, 0);
  const perPersonTotal = sampleAmount * 2;
  const percentageTotal = 2000 * (sampleAmount / 100);
  const isBookingDeadlineEnabled =
    formValues.expMode === EXPIRATION_MODE_DATE ||
    formValues.expMode === EXPIRATION_MODE_DAYS;

  const updateField = (field, value) => {
    onChange({
      ...formValues,
      [field]: value
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit();
  };

  const handleBookingDeadlineToggle = (event) => {
    const isEnabled = event.target.checked;

    onChange({
      ...formValues,
      expMode: isEnabled ? EXPIRATION_MODE_DATE : null,
      expirationDate: isEnabled ? formValues.expirationDate : '',
      expDays: isEnabled ? formValues.expDays : ''
    });
  };

  return (
    <tr className='discount-codes__form-row'>
      <td colSpan={6}>
        <form className='discount-codes__form' onSubmit={handleSubmit}>
          <div className='discount-codes__form-grid'>
            <div className='discount-codes__form-main'>
              <div className='discount-codes__field-grid'>
                <div className='discount-codes__field'>
                  <XGInput
                    id='discount-code-title-input'
                    name='title'
                    type='text'
                    label={t('settings:discountCodes.titleField', 'Title *')}
                    autoFocus={formMode === 'create'}
                    value={formValues.title}
                    placeholder={t(
                      'settings:discountCodes.titlePlaceholder',
                      'e.g. Spring returning customer promo'
                    )}
                    onChange={(event) =>
                      updateField('title', event.target.value)
                    }
                    inputProps={{
                      maxLength: 250
                    }}
                  />
                  <small>{`${formValues.title.length}/250`}</small>
                </div>
                <div className='discount-codes__field discount-codes__code-field'>
                  <XGInput
                    id='discount-code-code-input'
                    name='code'
                    type='text'
                    label={t(
                      'settings:discountCodes.codeField',
                      'Discount code *'
                    )}
                    className='discount-codes__code-input'
                    value={formValues.code}
                    placeholder={t(
                      'settings:discountCodes.codePlaceholder',
                      'e.g. SPRING10'
                    )}
                    error={Boolean(codeErrorMessage)}
                    hint={codeErrorMessage}
                    icon={Boolean(codeErrorMessage)}
                    customIcon={
                      codeErrorMessage ? (
                        <WarningCircle color='#F04438' size={16} />
                      ) : null
                    }
                    onChange={(event) =>
                      updateField('code', event.target.value.toUpperCase())
                    }
                    inputProps={{
                      maxLength: 100,
                      'aria-invalid': Boolean(codeErrorMessage)
                    }}
                  />
                  <small>{`${formValues.code.length}/100`}</small>
                  <aside className='discount-codes__sample'>
                    <strong>
                      {t(
                        'settings:discountCodes.sampleCalculation',
                        'Sample calculation'
                      )}
                    </strong>
                    {isPercentage ? (
                      <p>
                        {t('settings:discountCodes.samplePercentage', {
                          percent: formatCompactNumber(sampleAmount),
                          amount: percentageTotal.toFixed(2),
                          defaultValue:
                            '{{percent}}% x $2,000 base = ${{amount}}'
                        })}
                      </p>
                    ) : formValues.scope === DISCOUNT_SCOPE_PERSON ? (
                      <p>
                        {t('settings:discountCodes.samplePerPerson', {
                          amount: formatCompactNumber(sampleAmount),
                          total: formatCompactNumber(perPersonTotal),
                          defaultValue:
                            '2 passengers x ${{amount}} = ${{total}} total'
                        })}
                      </p>
                    ) : (
                      <p>
                        {t('settings:discountCodes.samplePerBooking', {
                          amount: formatCompactNumber(sampleAmount),
                          defaultValue: 'Flat ${{amount}} discount per booking'
                        })}
                      </p>
                    )}
                  </aside>
                </div>
              </div>

              <div className='discount-codes__config-row'>
                <label className='discount-codes__control-group'>
                  <span>
                    {t(
                      'settings:discountCodes.discountType',
                      'Discount type *'
                    )}
                  </span>
                  <SegmentedToggle
                    selectedValue={formValues.discountType}
                    onChange={(value) => {
                      onChange({
                        ...formValues,
                        discountType: value,
                        scope:
                          value === DISCOUNT_TYPE_FIXED
                            ? formValues.scope || DISCOUNT_SCOPE_BOOKING
                            : null,
                        inclAddons:
                          value === DISCOUNT_TYPE_PERCENTAGE
                            ? formValues.inclAddons
                            : false
                      });
                    }}
                    options={[
                      {
                        label: t(
                          'settings:discountCodes.fixedAmount',
                          'Fixed Amount ($)'
                        ),
                        value: DISCOUNT_TYPE_FIXED
                      },
                      {
                        label: t(
                          'settings:discountCodes.percentOff',
                          'Percentage (%)'
                        ),
                        value: DISCOUNT_TYPE_PERCENTAGE
                      }
                    ]}
                  />

                  {isPercentage ? (
                    <div className='discount-codes__checkbox-item discount-codes__include-addons-option'>
                      <XGCheckBox
                        id='discount-code-incl-addons'
                        name='inclAddons'
                        size='medium'
                        checked={formValues.inclAddons}
                        onChange={(event) =>
                          updateField('inclAddons', event.target.checked)
                        }
                      />
                      <label
                        className='discount-codes__checkbox-label'
                        htmlFor='discount-code-incl-addons'
                      >
                        {t(
                          'settings:discountCodes.includeAddons',
                          'Include add-ons in discount calculation'
                        )}
                      </label>
                    </div>
                  ) : null}
                </label>

                <label className='discount-codes__amount-field'>
                  <span>{t('settings:discountCodes.amount', 'Amount *')}</span>
                  <div className='discount-codes__amount-input'>
                    {!isPercentage && <span>$</span>}
                    <input
                      type='text'
                      inputMode='decimal'
                      value={formValues.discountValue}
                      placeholder={t(
                        isPercentage
                          ? 'settings:discountCodes.percentageAmountPlaceholder'
                          : 'settings:discountCodes.fixedAmountPlaceholder',
                        isPercentage ? '25' : '100'
                      )}
                      onChange={(event) =>
                        updateField(
                          'discountValue',
                          sanitizeDecimalValue(event.target.value)
                        )
                      }
                      onWheel={(event) => event.currentTarget.blur()}
                    />
                    {isPercentage && <span>%</span>}
                  </div>
                </label>

                {!isPercentage && (
                  <label className='discount-codes__control-group'>
                    <span>
                      {t('settings:discountCodes.appliesTo', 'Applies to')}
                    </span>
                    <SegmentedToggle
                      selectedValue={formValues.scope || DISCOUNT_SCOPE_BOOKING}
                      onChange={(value) => updateField('scope', value)}
                      options={[
                        {
                          label: t(
                            'settings:discountCodes.perBooking',
                            'Per booking'
                          ),
                          value: DISCOUNT_SCOPE_BOOKING
                        },
                        {
                          label: t(
                            'settings:discountCodes.perPerson',
                            'Per person'
                          ),
                          value: DISCOUNT_SCOPE_PERSON
                        }
                      ]}
                    />
                  </label>
                )}
              </div>

              <div className='discount-codes__deadline-row'>
                <div className='discount-codes__deadline-section'>
                  <label className='discount-codes__deadline-toggle'>
                    <Toggle
                      id='discount-code-booking-deadline-toggle'
                      checked={isBookingDeadlineEnabled}
                      onChange={handleBookingDeadlineToggle}
                      disabled={isSubmitting}
                      inputProps={{
                        'aria-label': t(
                          'settings:discountCodes.bookingDeadline',
                          'Booking deadline'
                        )
                      }}
                    />
                    <span>
                      {t(
                        'settings:discountCodes.bookingDeadline',
                        'BookingDeadline'
                      )}
                    </span>
                  </label>

                  {isBookingDeadlineEnabled && (
                    <div className='discount-codes__deadline-controls'>
                      <SegmentedToggle
                        selectedValue={formValues.expMode}
                        onChange={(value) => updateField('expMode', value)}
                        options={[
                          {
                            label: t(
                              'settings:discountCodes.specificDate',
                              'Specific date'
                            ),
                            value: EXPIRATION_MODE_DATE
                          },
                          {
                            label: t(
                              'settings:discountCodes.daysBeforeTripStartLabel',
                              'Days before trip start date'
                            ),
                            value: EXPIRATION_MODE_DAYS
                          }
                        ]}
                      />
                    </div>
                  )}
                </div>

                {isBookingDeadlineEnabled && (
                  <div className='discount-codes__deadline-input'>
                    {formValues.expMode === EXPIRATION_MODE_DATE && (
                      <XGDatePicker
                        id='discount-code-expiration-date'
                        name='expirationDate'
                        className='discount-codes__date-picker'
                        format='dd MMM yyyy'
                        value={dateStringToDate(formValues.expirationDate)}
                        onChange={(selectedDate) =>
                          updateField(
                            'expirationDate',
                            dateToDateString(selectedDate)
                          )
                        }
                        slotProps={{ field: { clearable: true } }}
                      />
                    )}
                    {formValues.expMode === EXPIRATION_MODE_DAYS && (
                      <>
                        <XGInput
                          id='discount-code-exp-days'
                          name='expDays'
                          className='discount-codes__days-input'
                          type='text'
                          value={formValues.expDays}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                          }}
                          onChange={(event) =>
                            updateField(
                              'expDays',
                              event.target.value.replace(/\D/g, '')
                            )
                          }
                          style={{ width: '50px' }}
                        />
                        <span>
                          {t(
                            'settings:discountCodes.daysBeforeTripStartText',
                            'days before trip start date'
                          )}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className='discount-codes__checkbox-list'>
                <div className='discount-codes__checkbox-item'>
                  <XGCheckBox
                    id='discount-code-default-on-new-trips'
                    name='defaultOnNewTrips'
                    size='medium'
                    checked={formValues.defaultOnNewTrips}
                    onChange={(event) =>
                      updateField('defaultOnNewTrips', event.target.checked)
                    }
                  />
                  <label
                    className='discount-codes__checkbox-label'
                    htmlFor='discount-code-default-on-new-trips'
                  >
                    {t(
                      'settings:discountCodes.autoAssignToNewTrips',
                      'Auto-assign to new trips'
                    )}
                  </label>
                </div>
                <div className='discount-codes__checkbox-item'>
                  <XGCheckBox
                    id='discount-code-combinable'
                    name='combinable'
                    size='medium'
                    checked={formValues.combinable}
                    onChange={(event) =>
                      updateField('combinable', event.target.checked)
                    }
                  />
                  <label
                    className='discount-codes__checkbox-label'
                    htmlFor='discount-code-combinable'
                  >
                    {t(
                      'settings:discountCodes.combinable',
                      'Combinable with other codes'
                    )}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className='discount-codes__form-actions'>
            <XGButton
              id='discount-code-cancel-button'
              text={t('common:cancel', 'Cancel')}
              type='secondary'
              size='medium'
              variant='outlined'
              onClick={onCancel}
              disabled={isSubmitting}
            />
            <XGButton
              id='discount-code-submit-button'
              text={t('settings:discountCodes.saveChanges', 'Save changes')}
              type='primary'
              size='medium'
              variant='contained'
              onClick={onSubmit}
              disabled={
                isSubmitting ||
                !isFormValid(formValues) ||
                Boolean(codeErrorMessage)
              }
            />
          </div>
        </form>
      </td>
    </tr>
  );
};

DiscountCodeForm.propTypes = {
  formValues: PropTypes.object.isRequired,
  formMode: PropTypes.oneOf(['create', 'edit']).isRequired,
  onCancel: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  codeErrorMessage: PropTypes.string
};

const DiscountCodesPanel = ({ id }) => {
  const { t } = useTranslation(['settings', 'common', 'errors']);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: SORTABLE_COLUMNS.title,
    direction: 'asc'
  });
  const [openForm, setOpenForm] = useState(null);
  const [formValues, setFormValues] = useState(createEmptyDiscountCodeForm());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingDefaultIds, setPendingDefaultIds] = useState(() => new Set());

  const loadDiscountCodes = useCallback(
    async ({ showLoading = true } = {}) => {
      if (showLoading) setIsLoading(true);
      try {
        const response = await getDiscountCodes({ scope: 'global' });
        setDiscountCodes(
          extractDiscountCodeItems(response).map((item) =>
            normalizeDiscountCode(item)
          )
        );
      } catch (error) {
        ErrorToast(
          t('errors:failedToLoadDiscountCodes', 'Failed to load discount codes')
        );
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadDiscountCodes();
  }, [loadDiscountCodes]);

  const filteredDiscountCodes = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    const filtered = normalizedSearch
      ? discountCodes.filter((discountCode) => {
          const title = discountCode.title?.toLowerCase() || '';
          const code = discountCode.code?.toLowerCase() || '';
          return (
            title.includes(normalizedSearch) || code.includes(normalizedSearch)
          );
        })
      : [...discountCodes];

    return filtered.sort((a, b) => {
      const aValue = String(a[sortConfig.key] || '').toLowerCase();
      const bValue = String(b[sortConfig.key] || '').toLowerCase();
      const comparison = aValue.localeCompare(bValue);
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [discountCodes, searchText, sortConfig]);

  const hasDuplicateCode = useMemo(() => {
    const formCode = normalizeCodeValue(formValues.code);
    if (!formCode) return false;

    return discountCodes.some((discountCode) => {
      const sameCode = normalizeCodeValue(discountCode.code) === formCode;
      const sameDiscountCode =
        openForm?.discountCodeId !== undefined &&
        String(getDiscountCodeId(discountCode)) ===
          String(openForm.discountCodeId);

      return sameCode && !sameDiscountCode;
    });
  }, [discountCodes, formValues.code, openForm?.discountCodeId]);

  const codeErrorMessage = hasDuplicateCode
    ? t(
        'settings:discountCodes.duplicateCodeError',
        'This discount code already exists.'
      )
    : '';

  const setSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <CaretUpDown size={14} />;
    return sortConfig.direction === 'asc' ? (
      <CaretUp size={14} />
    ) : (
      <CaretDown size={14} />
    );
  };

  const closeForm = () => {
    setOpenForm(null);
    setFormValues(createEmptyDiscountCodeForm());
  };

  const openCreateForm = () => {
    setOpenForm({ mode: 'create' });
    setFormValues(createEmptyDiscountCodeForm());
  };

  const toggleEditForm = (discountCode) => {
    const discountCodeId = getDiscountCodeId(discountCode);

    if (
      openForm?.mode === 'edit' &&
      openForm?.discountCodeId === discountCodeId
    ) {
      closeForm();
      return;
    }

    setOpenForm({ mode: 'edit', discountCodeId });
    setFormValues(createDiscountCodeFormFromItem(discountCode));
  };

  const handleSubmitForm = async () => {
    if (!isFormValid(formValues)) {
      ErrorToast(
        t(
          'settings:discountCodes.validationError',
          'Please complete all required discount code fields.'
        )
      );
      return;
    }

    if (hasDuplicateCode) return;

    setIsSubmitting(true);
    try {
      const payload = buildEditableDiscountCodePayload(formValues);
      if (openForm?.mode === 'edit') {
        await updateDiscountCode(openForm.discountCodeId, payload);
      } else {
        await createDiscountCode(payload);
      }

      await loadDiscountCodes({ showLoading: false });
      closeForm();
      SuccessToast(t('settings:saved', 'Changes were saved successfully'));
    } catch (error) {
      ErrorToast(
        t('errors:failedToSaveDiscountCode', 'Failed to save discount code')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDefaultChange = async (discountCode, checked) => {
    const discountCodeId = getDiscountCodeId(discountCode);
    const previousDefaultOnNewTrips = Boolean(discountCode.defaultOnNewTrips);
    const payload = buildEditableDiscountCodePayload({
      ...createDiscountCodeFormFromItem(discountCode),
      defaultOnNewTrips: checked
    });

    setPendingDefaultIds((current) => new Set(current).add(discountCodeId));
    setDiscountCodes((current) =>
      current.map((item) =>
        getDiscountCodeId(item) === discountCodeId
          ? { ...item, defaultOnNewTrips: checked }
          : item
      )
    );

    try {
      await updateDiscountCode(discountCodeId, payload);
      await loadDiscountCodes({ showLoading: false });
    } catch (error) {
      setDiscountCodes((current) =>
        current.map((item) =>
          getDiscountCodeId(item) === discountCodeId
            ? { ...item, defaultOnNewTrips: previousDefaultOnNewTrips }
            : item
        )
      );
      ErrorToast(
        t('errors:failedToSaveDiscountCode', 'Failed to save discount code')
      );
    } finally {
      setPendingDefaultIds((current) => {
        const next = new Set(current);
        next.delete(discountCodeId);
        return next;
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setIsSubmitting(true);
    try {
      const discountCodeId = getDiscountCodeId(deleteTarget);
      await deleteDiscountCode(discountCodeId);
      await loadDiscountCodes({ showLoading: false });
      if (openForm?.discountCodeId === discountCodeId) closeForm();
      setDeleteTarget(null);
      SuccessToast(
        t('settings:discountCodes.deleted', 'Discount code deleted')
      );
    } catch (error) {
      ErrorToast(
        t('errors:failedToDeleteDiscountCode', 'Failed to delete discount code')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id={id} className='discount-codes'>
      <div className='discount-codes__search-action'>
        <div className='discount-codes__search-wrap'>
          <XGInput
            id='discount-codes-search'
            className='discount-codes__search'
            label={null}
            name='discount-codes-search'
            placeholder={t(
              'settings:discountCodes.searchPlaceholder',
              'Search by title or code'
            )}
            type='text'
            value={searchText}
            onChange={(event) => setSearchText(event?.target?.value ?? '')}
          />
        </div>
        <XGButton
          id='add-discount-code-button'
          text={t(
            'settings:discountCodes.addDiscountCode',
            'Add Discount Code'
          )}
          type='secondary'
          size='medium'
          variant='outlined'
          onClick={openCreateForm}
          disabled={isSubmitting}
          startIcon={<PlusCircle />}
        />
      </div>

      <div className='discount-codes__table-wrap'>
        <table className='discount-codes__table'>
          <thead>
            <tr>
              <th>
                {t(
                  'settings:discountCodes.defaultOnNewTripsColumn',
                  'Default to new trips'
                )}
              </th>
              <th>
                <button
                  type='button'
                  onClick={() => setSort(SORTABLE_COLUMNS.title)}
                >
                  {t('settings:discountCodes.titleColumn', 'Title')}
                  {getSortIcon(SORTABLE_COLUMNS.title)}
                </button>
              </th>
              <th>
                <button
                  type='button'
                  onClick={() => setSort(SORTABLE_COLUMNS.code)}
                >
                  {t('settings:discountCodes.codeColumn', 'Code')}
                  {getSortIcon(SORTABLE_COLUMNS.code)}
                </button>
              </th>
              <th>{t('settings:discountCodes.valueColumn', 'Value')}</th>
              <th>
                {t(
                  'settings:discountCodes.bookingDeadline',
                  'Booking deadline'
                )}
              </th>
              <th aria-label={t('common:delete', 'Delete')}></th>
            </tr>
          </thead>
          <tbody>
            {openForm?.mode === 'create' && (
              <DiscountCodeForm
                formValues={formValues}
                formMode='create'
                onCancel={closeForm}
                onChange={setFormValues}
                onSubmit={handleSubmitForm}
                isSubmitting={isSubmitting}
                codeErrorMessage={codeErrorMessage}
              />
            )}

            {isLoading && (
              <tr>
                <td colSpan={6} className='discount-codes__state'>
                  <CircularProgress size={24} />
                </td>
              </tr>
            )}

            {!isLoading &&
              filteredDiscountCodes.map((discountCode) => {
                const discountCodeId = getDiscountCodeId(discountCode);
                const isEditOpen =
                  openForm?.mode === 'edit' &&
                  openForm?.discountCodeId === discountCodeId;

                return (
                  <Fragment key={discountCodeId}>
                    <tr
                      className={isEditOpen ? 'discount-codes__row--open' : ''}
                    >
                      <td>
                        <XGCheckBox
                          id={`discount-code-default-${discountCodeId}`}
                          name={`discount-code-default-${discountCodeId}`}
                          size='medium'
                          checked={discountCode.defaultOnNewTrips}
                          disabled={
                            isSubmitting ||
                            pendingDefaultIds.has(discountCodeId)
                          }
                          onChange={(event) =>
                            handleDefaultChange(
                              discountCode,
                              event.target.checked
                            )
                          }
                        />
                      </td>
                      <td onClick={() => toggleEditForm(discountCode)}>
                        <span className='discount-codes__title-cell'>
                          {discountCode.title}
                        </span>
                      </td>
                      <td onClick={() => toggleEditForm(discountCode)}>
                        <span className='discount-codes__code-cell'>
                          {discountCode.code}
                        </span>
                      </td>
                      <td onClick={() => toggleEditForm(discountCode)}>
                        {formatDiscountValue(discountCode)}
                      </td>
                      <td onClick={() => toggleEditForm(discountCode)}>
                        {getDeadlineLabel(discountCode, t)}
                      </td>
                      <td>
                        <IconButton
                          className='discount-codes__delete-button'
                          aria-label={t('common:delete', 'Delete')}
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteTarget(discountCode);
                          }}
                          disabled={isSubmitting}
                          size='small'
                        >
                          <Trash size={20} />
                        </IconButton>
                      </td>
                    </tr>
                    {isEditOpen && (
                      <DiscountCodeForm
                        formValues={formValues}
                        formMode='edit'
                        onCancel={closeForm}
                        onChange={setFormValues}
                        onSubmit={handleSubmitForm}
                        isSubmitting={isSubmitting}
                        codeErrorMessage={codeErrorMessage}
                      />
                    )}
                  </Fragment>
                );
              })}

            {!isLoading && filteredDiscountCodes.length === 0 && (
              <tr className='discount-codes__state-row'>
                <td colSpan={6} className='discount-codes__state'>
                  {t(
                    'settings:discountCodes.noDiscountCodes',
                    'No discount codes found'
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <DeleteModal
        id='delete-discount-code-modal'
        isOpenModal={Boolean(deleteTarget)}
        title={t('settings:discountCodes.deleteTitle', 'Delete discount code')}
        description={`${t(
          'settings:discountCodes.deleteConfirmation',
          'Are you sure you want to delete this discount code?'
        )} ${t(
          'common:thisActionCannotBeUndone',
          'This action cannot be undone.'
        )}`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        isSubmitting={isSubmitting}
      />
    </section>
  );
};

DiscountCodesPanel.propTypes = {
  id: PropTypes.string
};

export default DiscountCodesPanel;
